import { doc, getDoc, getFirestore, serverTimestamp, setDoc } from "firebase/firestore";
import firebaseApp from "../../firebase/credenciales";
import { CONT_COLLECTIONS } from "./collections";
import { SIRE_ESTADOS } from "./sireEstados";
import { actualizarEstadoSirePeriodo, buildPeriodoDocId, obtenerEstadoSirePeriodo } from "./sirePeriodoService";

const db = getFirestore(firebaseApp);

const cleanText = (v) => (v == null ? "" : String(v).trim());

export const obtenerCierreSire = async ({ rucEmpresa, periodo, tipo }) => {
  const docId = buildPeriodoDocId({ rucEmpresa, periodo, tipo });
  const docRef = doc(db, CONT_COLLECTIONS.SIRE_CIERRES, docId);
  const snap = await getDoc(docRef);

  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
};

export const puedeModificarPeriodoSire = async ({ rucEmpresa, periodo, tipo }) => {
  const st = await obtenerEstadoSirePeriodo({ rucEmpresa, periodo, tipo });
  return st.estado !== SIRE_ESTADOS.CERRADO;
};

export const assertPeriodoEditable = async ({ rucEmpresa, periodo, tipo }) => {
  const editable = await puedeModificarPeriodoSire({ rucEmpresa, periodo, tipo });
  if (!editable) {
    throw new Error(`El período ${periodo} (${tipo}) está CERRADO y no permite modificaciones de comprobantes ni archivos.`);
  }
  return true;
};

export const cerrarPeriodoSire = async ({
  rucEmpresa,
  periodo,
  tipo,
  presentacionObj,
  usuario,
  motivo = "Presentación SIRE aceptada por SUNAT",
}) => {
  const cleanRuc = cleanText(rucEmpresa);
  const cleanPer = cleanText(periodo);
  const cleanTip = cleanText(tipo).toUpperCase();
  const docId = buildPeriodoDocId({ rucEmpresa: cleanRuc, periodo: cleanPer, tipo: cleanTip });

  const st = await obtenerEstadoSirePeriodo({ rucEmpresa: cleanRuc, periodo: cleanPer, tipo: cleanTip });
  if (st.estado !== SIRE_ESTADOS.ACEPTADO && st.estado !== SIRE_ESTADOS.CERRADO) {
    throw new Error(`Sólo se puede cerrar un período cuando SUNAT ha ACEPTADO la presentación. Estado actual: '${st.estado}'.`);
  }

  const cierreDocRef = doc(db, CONT_COLLECTIONS.SIRE_CIERRES, docId);

  const payload = {
    ruc_empresa: cleanRuc,
    periodo: cleanPer,
    tipo: cleanTip,
    estado: SIRE_ESTADOS.CERRADO,
    presentacion_id: cleanText(presentacionObj?.id || `${docId}_001_P1`),
    archivo_id: cleanText(presentacionObj?.archivo_id || `${docId}_001`),
    version_presentada: Number(presentacionObj?.version || 1),
    hash_sha256: cleanText(presentacionObj?.hash_sha256 || ""),
    ticket_sunat: cleanText(presentacionObj?.ticket || ""),
    estado_sunat: "ACEPTADO",
    motivo_cierre: cleanText(motivo),
    cerrado_at: serverTimestamp(),
    cerrado_por: cleanText(usuario),
  };

  await setDoc(cierreDocRef, payload, { merge: true });

  await actualizarEstadoSirePeriodo({
    rucEmpresa: cleanRuc,
    periodo: cleanPer,
    tipo: cleanTip,
    nuevoEstado: SIRE_ESTADOS.CERRADO,
    metadata: {
      archivo_aceptado: true,
      periodo_cerrado: true,
    },
  });

  return { docId, ...payload };
};

export const reabrirPeriodoSire = async ({
  rucEmpresa,
  periodo,
  tipo,
  usuario,
  motivo = "Reapertura explícita autorizada para correcciones",
}) => {
  const cleanRuc = cleanText(rucEmpresa);
  const cleanPer = cleanText(periodo);
  const cleanTip = cleanText(tipo).toUpperCase();
  const docId = buildPeriodoDocId({ rucEmpresa: cleanRuc, periodo: cleanPer, tipo: cleanTip });

  const st = await obtenerEstadoSirePeriodo({ rucEmpresa: cleanRuc, periodo: cleanPer, tipo: cleanTip });
  if (st.estado !== SIRE_ESTADOS.CERRADO) {
    throw new Error(`El período '${cleanPer}' (${cleanTip}) no está en estado CERRADO.`);
  }

  const cierreDocRef = doc(db, CONT_COLLECTIONS.SIRE_CIERRES, docId);
  const payloadReapertura = {
    estado: SIRE_ESTADOS.REABIERTO,
    reabierto_at: serverTimestamp(),
    reabierto_por: cleanText(usuario),
    motivo_reapertura: cleanText(motivo),
  };

  await setDoc(cierreDocRef, payloadReapertura, { merge: true });

  await actualizarEstadoSirePeriodo({
    rucEmpresa: cleanRuc,
    periodo: cleanPer,
    tipo: cleanTip,
    nuevoEstado: SIRE_ESTADOS.REABIERTO,
    metadata: {
      periodo_cerrado: false,
    },
  });

  return { docId, ...payloadReapertura };
};
